import styles from './Tab.module.scss';
import AvatarWithMarketplace from '@components/AvatarWithMarketplace';
import BeforeCustomsTable from '../components/BeforeCustomsTable/BeforeCustomsTable';
import Box from '@components/Box';
import cn from 'classnames';
import DataTable, { BeforeProps, DataViewHandle } from '@containers/DataTable';
import DisposalProcessingModalWindow from '../modals/DisposalProcessingModalWindow';
import Flag from '@components/Flag';
import InfoIcon from '@assets/icons/info-grey-18.svg';
import ParcelModalWindow from '@containers/ParcelModalWindow';
import PaymentCompletedModalWindow from '@components/modals/PaymentCompletedModalWindow';
import PaymentModalWindow from '@components/modals/PaymentModalWindow';
import PlacingOrderModalWindow from '@components/modals/PlacingOrderModalWindow';
import ProgressBar from '@components/ProgressBar';
import Reason from '../components/Reason';
import TableSelectionPanel from '@components/Table/TableSelectionPanel';
import TrackingNumber from "@components/TrackingNumber";
import { actionAddresses } from '@utils/handlerSubmitActions';
import { AddSellerPanel } from '@components/AddSeller';
import { Button, ButtonSize, ButtonType } from '@components/Button';
import {
  checkBalanceReplenishment,
  checkBalanceReplenishmentResponse,
  getPaymentLink,
  getWallet,
  sendTopUpBalance,
  sendTopUpBalanceResponse,
  TGetPaymentLinkResponse
} from '@api/apiClient/finance';
import {
  checkReturnOrderStatus,
  getProblematicCustomsParcels,
  sendCustomsReturnOrder,
  TCheckReturnOrderStatusResponse,
  TProblematicCustomsParcel,
  TReexportTariffParams,
  TReturnOrderAddress,
  TReturnOrderParams,
  TReturnOrderResponse
} from '@api/apiClient/problematic';
import { Column, Row } from '@components/Grid';
import { createSumFromObject, formatSumWithCurrency } from '@utils/formatValues';
import {
  EPlacingOrder,
  TPlacingOrderData,
  TPlacingOrderDataItem
} from '@components/modals/PlacingOrderModalWindow/types';
import {
  EPlacingOrderViewParcel,
  EPlacingOrderViewParcelType,
  getPlacingOrderView
} from '@utils/createPlacingOrderView';
import { FilterModel } from '@hooks/useFiltersSearchParams';
import { Filters, FilterTypes } from '@containers/Filters';
import { getCurrentLanguage } from '@utils/lang';
import { getDaysBetweenDates, getProgressFromTo } from '@utils/progress';
import { ICustomsTabProps } from '../types';
import { Modal } from '@components/Modal';
import { TableColumn, TableColumnType, TableRowType } from '@components/Table/TableTypes';
import { Text, TextColor, TextSize, TextType } from '@components/Text';
import { Tooltip } from '@components/new/Tooltip';
import { TPlacingOrderListItem } from '@components/PlacingOrderView/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '@contexts/ProfileContext';
import { useTranslation } from 'react-i18next';

const CustomsTab: React.FC<ICustomsTabProps> = (props: ICustomsTabProps): React.ReactElement => {
  const {
    isFilterOpen,
    onUpdateCustoms,
    onUpdateDisposal,
    onUpdateFirstMile,
    onUpdateOnWay,
    reexportTariffsData,
    sellersNotFound,
    setIsFilterOpen
  } = props;

  const [innerFilters, setInnerFilters] = useState<FilterModel[]>([]);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isOpenDisposalProcessingWindow, setIsOpenDisposalProcessingWindow] = useState<boolean>(false);
  const [isOpenParcelWindow, setIsOpenParcelWindow] = useState(false);
  const [isOpenPaymentCompletedWindow, setIsOpenPaymentCompletedWindow] = useState<boolean>(false);
  const [isOpenPaymentWindow, setIsOpenPaymentWindow] = useState<boolean>(false);
  const [isOpenReturnProcessingWindow, setIsOpenReturnProcessingWindow] = useState<boolean>(false);
  const [orderInProcessed, setOrderInProcessed] = useState<boolean>(false);
  const [parcelId, setParcelId] = useState<string>('');
  const [parcelsAnyView, setParcelsAnyView] = useState<TPlacingOrderListItem[] | undefined>(undefined);
  const [parcelsGoodView, setParcelsGoodView] = useState<TPlacingOrderListItem[] | undefined>(undefined);
  const [paymentError, setPaymentError] = useState<string | undefined>(undefined);
  const [paymentInProcessed, setPaymentInProcessed] = useState<boolean>(false);
  const [selectedDelivery, setSelectedDelivery] = useState<number>(0);
  const [selectedExtraSmallBlocked, setSelectedExtraSmallBlocked] = useState<string[]>([]);
  const [selectedParcels, setSelectedParcels] = useState<Map<string, TProblematicCustomsParcel>>(new Map());
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [selectedSumRub, setSelectedSumRub] = useState<number>(0);
  const [submitParams, setSubmitParams] = useState<TReturnOrderParams | undefined>(undefined);

  const dataRef = useRef<DataViewHandle>(null);
  const timeoutCheckBalance = useRef<number | null>(null);
  const timeoutCheckPayment = useRef<number | null>(null);
  const timeoutCheckStatus = useRef<number | null>(null);

  const currentLanguage = getCurrentLanguage();
  const navigate = useNavigate();
  const { account, get } = useProfile();
  const { t } = useTranslation();

  const variableCurrency: string = account?.region === 'TR' ? '$' : '¥';

  const handleSelectionChange = useCallback((
      selectedIds: string[],
      items: TProblematicCustomsParcel[]
  ) => {
    setSelectedRowIds(selectedIds);

    const parcels: Map<string, TProblematicCustomsParcel> = new Map();

    selectedParcels.forEach((parcel: TProblematicCustomsParcel, key: string) => {
      const item = items.find((v: TProblematicCustomsParcel) => v.id === key);
      if (!item) parcels.set(key, parcel);
    });

    selectedIds.forEach((id) => {
      const item = items.find((v: TProblematicCustomsParcel) => (v.id === id));
      if (item) {
        parcels.set(id, item);
      } else {
        const selectedItem = selectedParcels.get(id);
        if (selectedItem) {
          parcels.set(id, selectedItem);
        }
      }
    });

    const parcelsArray: TProblematicCustomsParcel[] = Array.from(parcels.values());
    const parcelsAny: TProblematicCustomsParcel[] = parcelsArray.filter((item: TProblematicCustomsParcel) => item.isExtraSmall);
    const parcelsGood: TProblematicCustomsParcel[] = parcelsArray.filter((item: TProblematicCustomsParcel) => !item.isExtraSmall);

    const delivery = +parcelsGood.reduce((acc, v) => (acc + (v.reexportPrice?.amount ?? 0)), 0).toFixed(2);
    const sumRub = +parcelsGood.reduce((acc, v) => (acc + (v.declaredValue?.amount ?? 0)), 0).toFixed(2);

    setSelectedDelivery(delivery);
    setSelectedExtraSmallBlocked(parcelsAny.map((item: TProblematicCustomsParcel) => item.externalId));
    setSelectedParcels(parcels);
    setSelectedSumRub(sumRub);
  }, [selectedParcels]);

  useEffect(() => {
    const parcelsArray: TProblematicCustomsParcel[] = Array.from(selectedParcels.values());
    const parcelsAny: TProblematicCustomsParcel[] = parcelsArray.filter((item: TProblematicCustomsParcel) => item.isExtraSmall);
    const parcelsGood: TProblematicCustomsParcel[] = parcelsArray.filter((item: TProblematicCustomsParcel) => !item.isExtraSmall);

    const parcelsAnyView: TPlacingOrderListItem[] =
        getPlacingOrderView(parcelsAny, EPlacingOrderViewParcel.PROBLEMATIC_CUSTOMS_RETURN, {
          t,
          type: EPlacingOrderViewParcelType.ANY
        });

    const parcelsGoodView: TPlacingOrderListItem[] =
        getPlacingOrderView(parcelsGood, EPlacingOrderViewParcel.PROBLEMATIC_CUSTOMS_RETURN, {
          type: EPlacingOrderViewParcelType.GOOD
        });

    setParcelsAnyView(parcelsAnyView.length ? parcelsAnyView : undefined);
    setParcelsGoodView(parcelsGoodView);
  }, [currentLanguage, selectedParcels]);

  const onUpdateData = () => {
    if (dataRef.current?.refresh) {
      dataRef.current.refresh();
    }
    onUpdateCustoms();
    onUpdateDisposal();
    onUpdateFirstMile();
    onUpdateOnWay();
    setSelectedParcels(new Map());
    setSelectedRowIds([]);
  };

  const { mutate: mutateCheckBalanceReplenishment } = useMutation({
    mutationFn: checkBalanceReplenishment
  });

  const { mutate: mutateCheckBalance } = useMutation({
    mutationFn: getWallet,
    onSuccess: (result) => {
      if (result.balance >= selectedDelivery) {
        mutateSendCustomsReturnOrder(submitParams!);
      } else {
        waitingBalanceReplenished();
      }
    }
  });

  const { mutate: mutateCheckReturnOrderStatus } = useMutation({
    mutationFn: checkReturnOrderStatus
  });

  const {
    data: dataSendTopUpBalance,
    error: errorSendTopUpBalance,
    isError: isErrorSendTopUpBalance,
    isLoading: isLoadingSendTopUpBalance,
    mutate: mutateSendTopUpBalance,
    reset: resetSendTopUpBalance
  } = useMutation({
    mutationFn: sendTopUpBalance,
    onSuccess: (result: sendTopUpBalanceResponse) => {
      setIsOpenPaymentWindow(true);
      setIsOpenReturnProcessingWindow(false);
      setPaymentTimeout();
      waitingBalanceReplenished(result.outTradeNo);
    }
  });

  const {
    error: errorGetPaymentLink,
    isError: isErrorGetPaymentLink,
    isLoading: isLoadingGetPaymentLink,
    mutate: mutateGetPaymentLink
  } = useMutation({
    mutationFn: getPaymentLink,
    onSuccess: (result: TGetPaymentLinkResponse) => {
      setPaymentInProcessed(true);
      setPaymentTimeout();
      waitingBalanceReplenished();
      window.open(result.paymentLink);
    }
  });

  const {
    error: errorSendCustomsReturnOrder,
    isError: isErrorSendCustomsReturnOrder,
    isLoading: isLoadingSendCustomsReturnOrder,
    mutate: mutateSendCustomsReturnOrder,
    reset: resetSendCustomsReturnOrder
  } = useMutation({
    mutationFn: sendCustomsReturnOrder,
    onSuccess: (result: TReturnOrderResponse) => {
      setOrderInProcessed(true);
      waitingOrderStatus(result.orderId);
    }
  });

  const waitingBalanceReplenished = (id?: string): void => {
    timeoutCheckBalance.current = window.setTimeout(() => {
      if (id) {
        mutateCheckBalanceReplenishment(id, {
          onSuccess: (data: checkBalanceReplenishmentResponse) => {
            if (data.isProcess) {
              if (!paymentInProcessed) setPaymentInProcessed(true);
              waitingBalanceReplenished(id);
            } else {
              if (paymentInProcessed) setPaymentInProcessed(false);
              if (data.isSuccess) {
                clearTimeout(timeoutCheckPayment.current as number);
                mutateCheckBalance();
              } else {
                if (data.errorCode) {
                  clearTimeout(timeoutCheckPayment.current as number);
                  setPaymentError(data.errorCode);
                } else {
                  waitingBalanceReplenished(id);
                }
              }
            }
          }
        });
      } else {
        mutateCheckBalance();
      }
    }, 3000);
  };

  const waitingOrderStatus = (returnOrderId: string): void => {
    timeoutCheckStatus.current = window.setTimeout(() => {
      mutateCheckReturnOrderStatus(returnOrderId, {
        onSuccess: (data: TCheckReturnOrderStatusResponse) => {
          if (data.orderStatus === 'AwaitingPay' || data.orderStatus === 'PayFailed') {
            waitingOrderStatus(returnOrderId);
          }
          if (data.orderStatus === 'Paid') {
            get();
            onCloseReturnProcessing();
            onUpdateData();
            setIsOpenPaymentCompletedWindow(true);
            setIsOpenPaymentWindow(false);
            setOrderInProcessed(false);
            setPaymentInProcessed(false);
          }
        }
      });
    }, 3000);
  };

  const setPaymentTimeout = (): void => {
    timeoutCheckPayment.current = window.setTimeout(() => {
      clearTimeout(timeoutCheckBalance.current as number);
      setPaymentInProcessed(false);
    }, 600000);
  };

  const onClosePaymentReturn = (): void => {
    clearTimeout(timeoutCheckBalance.current as number);
    clearTimeout(timeoutCheckPayment.current as number);
    setIsOpenPaymentWindow(false);
    setPaymentInProcessed(false);
  };

  const onClickPaymentLink = (amount: string) => {
    mutateGetPaymentLink(amount);
  };

  const onCloseParcel = () => {
    setIsOpenParcelWindow(false);
  };

  const onCloseDisposalProcessing = () => {
    setIsOpenDisposalProcessingWindow(false);
  };

  const onCloseReturnProcessing = () => {
    resetSendCustomsReturnOrder();
    resetSendTopUpBalance();
    setIsOpenReturnProcessingWindow(false);
    setPaymentInProcessed(false);
  };

  const createTariffsTooltip = (reexportTariffs: TReexportTariffParams[]): string => (
      reexportTariffs[0].marketplaceType + ' ' + t(`countries.${reexportTariffs[0].countryCode}`) + ` — ${createSumFromObject(reexportTariffs[0].price)}` + '\n' +
      reexportTariffs[1].marketplaceType + ' ' + t(`countries.${reexportTariffs[1].countryCode}`) + ` — ${createSumFromObject(reexportTariffs[1].price)}` + '\n' +
      reexportTariffs[2].marketplaceType + ` ${t('problematicParcelsPage.and')} ` + reexportTariffs[4].marketplaceType + ` — ${createSumFromObject(reexportTariffs[2].price)}` + '\n' +
      reexportTariffs[6].marketplaceType + ` — ${createSumFromObject(reexportTariffs[6].price)}`
  );

  const handlerSubmit = (forms: TPlacingOrderData): void => {
    const addresses: TReturnOrderAddress[] = forms.map((item: TPlacingOrderDataItem) => ({
      address: item.address,
      contactInfo: {
        name: item.fullName,
        phone: item.phone
      }
    }));
    const params: TReturnOrderParams = {
      addresses: addresses,
      parcelIds: parcelsGoodView!.map((item: TPlacingOrderListItem) => item.id)
    };
    if (account!.balance >= selectedDelivery) {
      mutateSendCustomsReturnOrder(params);
    } else {
      setSubmitParams(params);
      if (account!.region === 'TR') {
        onCloseReturnProcessing();
        setIsOpenPaymentWindow(true);
      } else {
        mutateSendTopUpBalance({
          amount: Math.round((selectedDelivery - account!.balance) * 100) / 100
        });
      }
    }
    actionAddresses(forms);
  };

  const BeforeCustomsTableComponent = useCallback((props: BeforeProps<TProblematicCustomsParcel>) => {
    return <BeforeCustomsTable {...props} />;
  }, []);

  const columns: TableColumn<TProblematicCustomsParcel>[] = useMemo(() => [
    {
      header: t("common.trackingNumber"),
      accessor: (row: TableRowType<TProblematicCustomsParcel>) => (
        <TrackingNumber
          externalId={row.data?.externalId}
          trackNumber={row.data?.trackNumber}
          onClick={() => {
            setIsOpenParcelWindow(true);
            setParcelId(row.data?.id);
          }}
        />
      ),
      fixWidth: true,
      noOverflow: true,
      width: '200px'
    },
    {
      header: t('problematicParcelsPage.reason'),
      accessor: (row: TableRowType<TProblematicCustomsParcel>) => <Reason value={row.data.reason}/>
    },
    {
      header: t('problematicParcelsPage.service'),
      accessor: (row: TableRowType<TProblematicCustomsParcel>) => row.data.serviceName,
      width: '200px'
    },
    {
      header: t('problematicParcelsPage.price'),
      accessor: (row: TableRowType<TProblematicCustomsParcel>) => (
          <div className={styles.price}>
            <Text
                color={TextColor.PRIMARY}
                size={TextSize.S}
            >
              {createSumFromObject(row.data.declaredValue)}
            </Text>
            {/*<Text*/}
            {/*    color={TextColor.DARK_MUTED}*/}
            {/*    size={TextSize.XS}*/}
            {/*>*/}
            {/*  {createSumFromObject(row.data.declaredValue)}*/}
            {/*</Text>*/}
          </div>
      ),
      alignment: 'right',
      width: '120px'
    },
    {
      header: <div className={styles.title}>
        {t('problematicParcelsPage.sum')}
        {reexportTariffsData && (
            <Tooltip
                action={
                  <img
                      alt="Info"
                      src={InfoIcon}
                  />
                }
                position="top"
                text={createTariffsTooltip(reexportTariffsData.reexportTariffs)}
                whiteSpace="pre-wrap"
            />
        )}
      </div>,
      accessor: (row: TableRowType<TProblematicCustomsParcel>) => (
          <>
            {row.data.isExtraSmall ? (
                <Tooltip
                    action={
                      <div className={styles.actionPrice}>
                        {t('problematicParcelsPage.customs.no')}
                      </div>
                    }
                    position="top"
                    text={t('problematicParcelsPage.customs.state.info_1')}
                    whiteSpace="pre-wrap"
                />
            ) : (
                <div className={styles.actionPrice}>
                  <Flag countryCode={row.data.countryCode}/>
                  {createSumFromObject(row.data.reexportPrice)}
                </div>
            )}
          </>
      ),
      fixWidth: true,
      noOverflow: true,
      type: TableColumnType.PRIMARY,
      width: '100px'
    },
    {
      header: t('problematicParcelsPage.seller'),
      accessor: (row: TableRowType<TProblematicCustomsParcel>) => (
          <AvatarWithMarketplace
              id={row.data.sellerId}
              name={row.data.sellerName}
              marketplace={row.data.marketplaceType}
          />
      ),
      alignment: 'center',
      noOverflow: true
    },
    {
      header: t('problematicParcelsPage.toRecycle'),
      accessor: (row: TableRowType<TProblematicCustomsParcel>) => {
        const restDays = getDaysBetweenDates(null, row.data.estimatedUtilizationDate);
        return (
            <ProgressBar
                colors={[
                  { value: 300 / 7, main: '#1e3ebc', bg: '#e8ebf8', label: '#1e3ebc' },
                  { value: 0, main: '#ee6373', bg: '#fce0e3', label: '#ee6373' }
                ]}
                column
                label={restDays ? `${restDays} ${t('problematicParcelsPage.days')}` : t('problematicParcelsPage.lastDay')}
                progress={getProgressFromTo(row.data.createAt, row.data.estimatedUtilizationDate, true)}
            />
        );
      },
      width: '100px'
    }
  ], [currentLanguage, reexportTariffsData]);

  return (
      <div>
        {sellersNotFound ? (
            <AddSellerPanel
                isFirstSeller
                onSuccess={onUpdateData}
            />
        ) : (
            <>
              <Box pb={5}>
                <div>
                  <Text
                      color={TextColor.PRIMARY}
                      size={TextSize.S}
                      type={TextType.BLOCK}
                  >
                    {t('problematicParcelsPage.customs.description-1')}
                  </Text>
                  <Text
                      color={TextColor.PRIMARY}
                      size={TextSize.S}
                      type={TextType.BLOCK}
                  >
                    {t('problematicParcelsPage.customs.description-2')}
                  </Text>
                  <Text
                      color={TextColor.PRIMARY}
                      size={TextSize.S}
                      type={TextType.BLOCK}
                  >
                    {t('problematicParcelsPage.customs.description-3')}
                  </Text>
                  <Text
                      color={TextColor.PRIMARY}
                      size={TextSize.S}
                      type={TextType.BLOCK}
                  >
                    {t('problematicParcelsPage.customs.description-4')}
                  </Text>
                </div>
              </Box>
              <Box
                  pb={5}
                  className={cn(!isFilterOpen && styles.hidden)}
              >
                <Column phone={12}>
                  <Row
                      justify="right"
                      gap={5}
                  >
                    <Filters
                        id="problematic-customs"
                        openFilters={() => setIsFilterOpen(true)}
                        items={[
                          {
                            name: 'trackOrExternalNumbers',
                            type: FilterTypes.MULTI_SEARCH,
                            placeholder: t('filters.searchByTrackNumber')
                          },
                          {
                            name: 'serviceIds',
                            type: FilterTypes.SERVICE_PROBLEMATIC
                          },
                          {
                            name: 'sellerIds',
                            type: FilterTypes.SELLER
                          },
                          {
                            name: 'marketplaceType',
                            type: FilterTypes.MARKETPLACE
                          }
                        ]}
                        onChange={(v, isReady) => {
                          if (isReady) {
                            setIsInitialized(true);
                          }
                          setInnerFilters(v);
                        }}
                        values={innerFilters}
                        asPanel
                    />
                  </Row>
                </Column>
              </Box>
              <TableSelectionPanel
                  isShown={selectedRowIds.length > 0}
                  items={[
                    {
                      label: t('problematicParcelsPage.selected'),
                      value: selectedRowIds.length
                    },
                    {
                      label: t('problematicParcelsPage.forTheAmount'),
                      value: createSumFromObject({ amount: selectedSumRub, currency: 'RUB' })
                    }
                  ]}
                  buttons={[
                    (<Tooltip
                        action={
                          <img
                              alt="Info"
                              src={InfoIcon}
                          />
                        }
                        position="top"
                        key="tooltip"
                        text={
                          <div className={styles.clue}>
                            <span><b>{t('problematicParcelsPage.dispose')}</b></span>
                            <span>{t('problematicParcelsPage.disposeText')}</span>
                            {selectedRowIds.length !== selectedExtraSmallBlocked.length && (
                                <>
                                  <span><b>{t('problematicParcelsPage.return')}</b></span>
                                  <span>{t('problematicParcelsPage.returnText')}</span>
                                </>
                            )}
                          </div>
                        }
                    />),
                    (<Button
                        key="dispose"
                        onClick={() => setIsOpenDisposalProcessingWindow(true)}
                        size={ButtonSize.S}
                        type={ButtonType.RED_SKELETON}
                        title={t('problematicParcelsPage.dispose')}
                    />),
                    (<>
                      {selectedRowIds.length !== selectedExtraSmallBlocked.length && (
                          <Button
                              key="returnFor"
                              onClick={() => setIsOpenReturnProcessingWindow(true)}
                              size={ButtonSize.S}
                              type={ButtonType.PRIMARY}
                              title={t('problematicParcelsPage.returnFor') + ` ${formatSumWithCurrency(selectedDelivery, variableCurrency)}`}
                          />
                      )}
                    </>)
                  ]}
                  message={
                      selectedRowIds.length === selectedExtraSmallBlocked.length && (
                          <Text
                              size={TextSize.M}
                              type={TextType.BLOCK}
                              color={TextColor.ERROR_DARK}
                          >
                            {t('problematicParcelsPage.warning')}
                          </Text>
                      )
                  }
              >
                <DataTable<TProblematicCustomsParcel>
                    Before={BeforeCustomsTableComponent}
                    columns={columns}
                    dataRef={dataRef}
                    defaultPageSize={10}
                    filters={innerFilters}
                    getData={getProblematicCustomsParcels}
                    notFound={t('problematicParcelsPage.noParcels')}
                    onSelectionChange={handleSelectionChange}
                    pending={!isInitialized}
                    queryKey="getProblematicCustomsParcels"
                    selectable
                    selectedIds={selectedRowIds}
                />
              </TableSelectionPanel>
              <Modal
                  bodyWithoutIndentations
                  isOpen={isOpenParcelWindow}
                  onClickOverlay={onCloseParcel}
                  onClose={onCloseParcel}
                  width={900}
              >
                <ParcelModalWindow parcelId={parcelId} isProblematic />
              </Modal>
              <Modal
                  isOpen={isOpenDisposalProcessingWindow}
                  onClickOverlay={onCloseDisposalProcessing}
                  onClose={onCloseDisposalProcessing}
                  width={650}
              >
                <DisposalProcessingModalWindow
                    handlerOnCloseDisposalProcessing={onCloseDisposalProcessing}
                    handlerRefreshData={onUpdateData}
                    selectedRowIds={selectedRowIds}
                />
              </Modal>
              <Modal
                  isOpen={isOpenReturnProcessingWindow}
                  onClickOverlay={onCloseReturnProcessing}
                  onClose={onCloseReturnProcessing}
                  width={650}
              >
                <PlacingOrderModalWindow
                    costOfService={selectedDelivery}
                    errorSendPlacingOrder={errorSendCustomsReturnOrder}
                    errorSendTopUpBalance={errorSendTopUpBalance}
                    handlerOnClosePlacingOrderProcessing={onCloseReturnProcessing}
                    handlerResetSendOrder={resetSendCustomsReturnOrder}
                    handlerSubmit={handlerSubmit}
                    isErrorSendPlacingOrder={isErrorSendCustomsReturnOrder}
                    isErrorSendTopUpBalance={isErrorSendTopUpBalance}
                    isLoadingSendPlacingOrder={isLoadingSendCustomsReturnOrder}
                    isLoadingSendTopUpBalance={isLoadingSendTopUpBalance}
                    parcelsAny={parcelsAnyView}
                    parcelsGood={parcelsGoodView}
                    placingOrderInProcessed={orderInProcessed}
                    placingOrderType={EPlacingOrder.PROBLEMATIC_CUSTOMS}
                />
              </Modal>
              <Modal
                  isOpen={isOpenPaymentWindow}
                  onClickOverlay={onClosePaymentReturn}
                  onClose={onClosePaymentReturn}
                  width={560}
              >
                <PaymentModalWindow
                    dataSendTopUpBalance={dataSendTopUpBalance!}
                    errorGetPaymentLink={errorGetPaymentLink}
                    handlerOnClickPaymentLink={onClickPaymentLink}
                    isErrorGetPaymentLink={isErrorGetPaymentLink}
                    isLoadingGetPaymentLink={isLoadingGetPaymentLink}
                    isLoadingSendTopUpBalance={isLoadingSendTopUpBalance}
                    mutateSendTopUpBalance={mutateSendTopUpBalance}
                    paymentError={paymentError}
                    paymentInProcessed={paymentInProcessed}
                    selectedSum={selectedDelivery}
                    subTitle={t('paymentModalWindow.subTitle_1')}
                />
              </Modal>
              <Modal
                  isOpen={isOpenPaymentCompletedWindow}
                  onClickOverlay={() => setIsOpenPaymentCompletedWindow(false)}
                  onClose={() => setIsOpenPaymentCompletedWindow(false)}
                  width={560}
              >
                <PaymentCompletedModalWindow
                    action={() => navigate('/problematic-parcels?tab=OnWayTab')}
                    btnText={t('problematicParcelsPage.tabReturns')}
                    handlerSetIsOpenPaymentCompletedWindow={setIsOpenPaymentCompletedWindow}
                    subTitle={t('problematicParcelsPage.paymentCompletedModalWindow.subTitle', { tab: t('problematicParcelsPage.tabReturns') })}
                    title={t('paymentCompletedModalWindow.info_1')}
                />
              </Modal>
            </>
        )}
      </div>
  );
};

export default CustomsTab;
